import './styles/PhoneFrame.scss';

import { AnimatePresence, easeOut, motion, Variants } from 'motion/react';

import { useClassHelper } from '../../hooks';
import { IconRenderer } from '../IconRenderer';
import { PhoneFrameProps } from './types';

const componentName = 'c-PhoneFrame';

const phoneVariants = {
  initial: {
    opacity: 0,
    x: 10,
  },
  animate: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.15,
      easeOut,
    },
  },
  exit: {
    opacity: 0,
    x: -10,
    transition: {
      duration: 0.15,
      easeOut,
    },
  },
} as unknown as Variants;

export const PhoneFrame = ({
  children,
  loading,
  variant = 'default',
}: PhoneFrameProps) => {
  const c = useClassHelper(componentName);

  const renderPhoneContent = (isAndroid: boolean) => {
    const borderRadius = isAndroid ? 'rounded-xl' : 'rounded-[2.5rem]';
    const innerBorderRadius = isAndroid ? 'rounded-none' : 'rounded-[26px]';
    const contentHeight = isAndroid ? 'h-[678px]' : 'h-[677px]';
    const innerPadding = isAndroid ? 'pt-1' : 'pt-2';

    return (
      <motion.div
        key={variant}
        variants={phoneVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        className={`custom-sticky-position sticky top-0 mx-auto border-phone-frame-default bg-phone-frame-default border-[14px] ${borderRadius} h-[724px] w-[362px] shadow-xl scale-3d overflow-hidden`}
      >
        {/* Notch */}
        <div className="w-[148px] h-[18px] bg-phone-frame-default top-0 rounded-b-[1rem] left-1/2 -translate-x-1/2 absolute"></div>

        {/* Lateral buttons */}
        {isAndroid && (
          <div className="h-[32px] w-[3px] bg-phone-frame-default absolute -start-[17px] top-[72px] rounded-s-lg"></div>
        )}
        <div className="h-[46px] w-[3px] bg-phone-frame-default absolute -start-[17px] top-[124px] rounded-s-lg"></div>
        <div className="h-[46px] w-[3px] bg-phone-frame-default absolute -start-[17px] top-[178px] rounded-s-lg"></div>
        <div className="h-[64px] w-[3px] bg-phone-frame-default absolute -end-[17px] top-[142px] rounded-e-lg"></div>

        {/* Screen */}
        <div
          className={`${innerBorderRadius} overflow-hidden w-[334px] h-[695px] bg-device-system-black dark:bg-phone-frame-default ${innerPadding} pb-2`}
        >
          {/* Status bar */}
          <div className="h-[18px] top-0 pl-3 pr-1">
            <div className="flex justify-between items-center px-4 text-xs text-white bg-transparent w-full font-medium">
              <div>11:30</div>

              <div className="flex items-center gap-1">
                {/* Signal bars */}
                <div className="flex gap-[1px] items-end">
                  <div className="w-[2px] h-[6px] bg-white rounded-sm"></div>
                  <div className="w-[2px] h-[8px] bg-white rounded-sm"></div>
                  <div className="w-[2px] h-[10px] bg-white rounded-sm"></div>
                  <div className="w-[2px] h-[12px] bg-white rounded-sm"></div>
                </div>

                {/* WiFi icon */}
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path d="M5 12.55a11 11 0 0 1 14.08 0M8.53 16.11a6 6 0 0 1 6.95 0M12 20h.01"></path>
                </svg>

                {/* Battery - different for Android */}
                {isAndroid ? (
                  <div className="relative w-2 h-3 border border-white rounded-xs">
                    <div className="absolute bottom-0 left-0 w-full h-2 bg-white rounded-b-xs"></div>
                  </div>
                ) : (
                  <div className="flex items-center gap-[1px]">
                    <div className="w-[20px] h-[10px] border border-white rounded-sm relative">
                      <div className="absolute left-[1px] top-[1px] h-[7px] w-[10px] bg-white rounded-sm"></div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Content */}
          <div className={contentHeight}>
            {loading ? (
              <div className="flex h-full items-center justify-center">
                <IconRenderer variant="spinner-light" color="blue" />
              </div>
            ) : (
              children
            )}
          </div>

          {/* Home indicator (only iOS) */}
          {!isAndroid && <div className="h-[78px] top-0 pl-3 pr-1"></div>}
        </div>

        {/* Home bar (only iOS) */}
        {!isAndroid && (
          <div className="w-[148px] h-[4px] bg-phone-frame-default bottom-2 rounded-[1rem] left-1/2 -translate-x-1/2 absolute z-[11]"></div>
        )}
      </motion.div>
    );
  };

  return (
    <div className={`${c('base')} p-[16px]`}>
      <AnimatePresence mode="wait">
        {renderPhoneContent(variant === 'android')}
      </AnimatePresence>
    </div>
  );
};
